import {useQueryClient} from '@tanstack/react-query';
import {
	BeneficiaryModel,
	BeneficiarySituationEnum,
} from 'domain/usecases/others/selection-flow/beneficiary-v3';
import {getAccessToken} from 'infra/http/utils';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {HealthCardEnum, SelectionFlowEnum, UserTypeEnum} from 'presentation/constants';
import {useSelection} from 'presentation/pages/others/selection-flow/hooks';
import {useGetBeneficiaries} from 'presentation/pages/others/selection-flow/queries/useGetBeneficiaries';
import {useIsComingFromInitialFlowStore} from 'presentation/pages/others/selection-flow/store/useIsComingFromInitialFlowStore';
import {useSelectedBeneficiaryStore} from 'presentation/pages/others/selection-flow/store/useSelectedBeneficiaryStore';
import {useSelectedPlanStore} from 'presentation/pages/others/selection-flow/store/useSelectedPlanStore';
import {useConfirmCommunicationHasErrorStore} from 'presentation/pages/private/communications/store/useConfirmCommunicationHasErrorStore';
import {useAddressUpdateModalDisplayStore} from 'presentation/pages/private/home/store/useAddressUpdateModalDisplayStore';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {useRegistrationUpdateStatusStore} from 'presentation/pages/private/store/useRegistrationUpdateStatusStore';
import {useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

export const useBeneficiarySelection = () => {
	const selectedBeneficiary = useSelectedBeneficiaryStore(state => state.selectedBeneficiary);
	const setSelectedBeneficiary = useSelectedBeneficiaryStore(
		state => state.setSelectedBeneficiary,
	);
	const resetRegistrationUpdateStore = useRegistrationUpdateStatusStore(
		state => state.resetRegistrationUpdateStore,
	);
	const resetAddressUpdateModalDisplayControl = useAddressUpdateModalDisplayStore(
		state => state.resetDisplayControl,
	);
	const setAccountData = useAccountDataStore(state => state.setAccountData);

	const selectedPlan = useSelectedPlanStore(state => state.selectedPlan);
	const {confirmSelection, isSubmitting} = useSelection();

	const [showChangePlanDialogState, setShowChangePlanDialogState] = useState<boolean>(false);
	const isComingFromInitialFlow = useIsComingFromInitialFlowStore(
		state => state.isComingFromInitialFlow,
	);

	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const accessToken = getAccessToken();

	const {state} = useLocation();
	const isComingFromWalletPath = state?.pathFrom === HealthCardEnum.HEALTH_CARD;
	const {data: beneficiariesList, isLoading: beneficiaryListIsLoading} = useGetBeneficiaries({
		nuContrato: selectedPlan?.nuContrato,
	});

	const setConfirmCommunicationHasError = useConfirmCommunicationHasErrorStore(
		data => data.setConfirmCommunicationHasError,
	);

	const isPlanCancelledDatesAreEqual = Boolean(
		beneficiariesList?.every(
			(currentBeneficiary, _index, prevListBeneficiaries) =>
				currentBeneficiary?.dtCancelamentoC === prevListBeneficiaries[0]?.dtCancelamentoC,
		),
	);

	const isCompareSelectedBeneficiary = (value: BeneficiaryModel) =>
		JSON.stringify(selectedBeneficiary) === JSON.stringify(value);

	const handleSelectBeneficiary = (beneficiary: BeneficiaryModel) => {
		// if (isAccessBlocked(beneficiary)) return;

		setSelectedBeneficiary(beneficiary);
		resetRegistrationUpdateStore();
		queryClient.invalidateQueries({
			predicate: ({queryKey}) =>
				queryKey.includes(QUERY_KEYS.ADDRESS_UPDATE_INDICATOR) ||
				queryKey.includes(QUERY_KEYS.ADDRESS_SUGGESTION),
		});
		resetAddressUpdateModalDisplayControl();
		setAccountData({
			beneficiariesLength: beneficiariesList?.length || 1,
			planBeneficiaries: beneficiariesList ?? [],
		});

		setConfirmCommunicationHasError(false);
		if (accessToken) {
			confirmSelection({beneficiary, beneficiariesCount: beneficiariesList?.length});
		} else {
			setTimeout(() => {
				navigate(SelectionFlowEnum.CONFIRM_SELECTION, {state});
			}, 800);
		}
	};

	const handleGoToPlanSelection = () => {
		if (isComingFromInitialFlow) {
			setShowChangePlanDialogState(true);
			return;
		}
		navigate(SelectionFlowEnum.PLAN_SELECTION);
	};

	const holder = beneficiariesList?.find(
		beneficiary => beneficiary?.tipoUsuarioC === UserTypeEnum.HOLDER,
	);

	const dependents = beneficiariesList
		?.filter(beneficiary => beneficiary?.tipoUsuarioC === UserTypeEnum.DEPENDENT)
		?.sort((firstBeneficiary, secondBeneficiary) => {
			const firstBeneficiaryHasCancelDate = firstBeneficiary?.dtCancelamentoC ? 1 : 0;
			const secondBeneficiaryHasCancelDate = secondBeneficiary?.dtCancelamentoC ? 1 : 0;
			return firstBeneficiaryHasCancelDate - secondBeneficiaryHasCancelDate;
		});

	type Section = {
		title: string;
		items: BeneficiaryModel[];
	};

	let sections: Section[] = [];

	if (holder) {
		sections?.push({title: holder?.tipoUsuarioC, items: [holder]});
	}

	if (dependents && dependents?.length > 0) {
		const IS_SINGLE_DEPENDENT = dependents.length === 1;
		const title = `${dependents[0]?.tipoUsuarioC}${IS_SINGLE_DEPENDENT ? '' : 's'}`;
		sections?.push({title, items: dependents});
	}

	if (isComingFromInitialFlow) {
		sections = sections
			?.map(section => ({
				...section,
				items: section?.items?.filter(
					beneficiary =>
						!beneficiary?.flTitularSemDireito &&
						beneficiary?.situacao !== BeneficiarySituationEnum.CANCELLED,
				),
			}))
			.filter(section => section?.items?.length > 0);
	}

	if (isComingFromWalletPath) {
		sections = sections
			?.map(section => ({
				...section,
				items: section?.items?.filter(beneficiary => !beneficiary?.flTitularSemDireito),
			}))
			.filter(section => section?.items?.length > 0);
	}
	return {
		beneficiaryListIsLoading,
		isSubmitting,
		beneficiariesList,
		selectedPlan,
		handleGoToPlanSelection,
		isCompareSelectedBeneficiary,
		handleSelectBeneficiary,
		isPlanCancelledDatesAreEqual,
		showChangePlanDialogState,
		setShowChangePlanDialogState,
		sections,
	};
};
